<script setup>
import { WdsBreadCrumbs } from '@wds/components';
import { SEGMENT_PAGE_DATA } from '@/config/analytics';
import { APPDOWNLOADBANNERLINKS } from '@/config/app-download-banner-links';
import { PROPERTY_TYPE_DEFAULT } from '@/config/property-types';

definePageMeta({
  middleware: 'feature-gate',
  featureGate: 'web_enableOGevents',
});

// ### COMPOSABLES
const { $tracking } = useNuxtApp();
const {
  t,
  locale,
} = useI18n();
const route = useRoute();
const { useUrl } = useUrls();
const { useFormatter } = useFormatters();
const { isExtraSmallScreen } = useMq();
const { buildSeo } = generateSeo();
const {
  defineEventSchema,
  defineFaqSchema,
} = generateSchemas();
const {
  buildEventDetailsModel,
  getEventPageUrlFromRawEvent,
} = useEventDetailsModel();

// ### COMPOSABLES - APIs
const {
  getEventByEventId,
  getEventsByCityNameAndCountryName,
} = useApiEvents();
const {
  getTopPropertiesByCityId,
  getCountryTopCities,
} = useApiCities();

// ### STORES
const sessionStore = useSessionStore();
const testingStore = useTestingStore();

// ### EXPERIMENTS
const isEventsContinentPageEnabled = testingStore.getFeatureVariables('web_enableOGevents')?.continent_enabled || false;
const isEventsCountryPageEnabled = testingStore.getFeatureVariables('web_enableOGevents')?.country_enabled || false;

// ### CONSTANTS INITIALIZATION
const locationKey = 'eventdetails';
const segmentTracking = SEGMENT_PAGE_DATA.eventDetails;
const urlParams = route?.params || null;
const actualPropertyTypeObj = PROPERTY_TYPE_DEFAULT;
const GRAB_THE_APP_LINKS = APPDOWNLOADBANNERLINKS.EVENTS.DETAILS;
const appDownloadTopBannerDetails = {
  page: 'event-details',
  linkPaths: APPDOWNLOADBANNERLINKS?.EVENTS?.DETAILS?.mobile,
  linkParams: 'search',
};
const EVENTSLIMIT = 8;

// ### VARIABLES INITIALIZATION
const eventObj = await getEventByEventId(urlParams?.eventId);
if (!eventObj?.eventId) {
  throw createError({
    statusCode: 404,
    statusMessage: `### No such event ${urlParams?.eventId}`,
  });
}

// Note: Execute API calls in parallel (since they don't depend on each other)
// in order to reduce Time to First Byte (TTFB)
const [topPropertiesInLocation, topEventsInLocation, topCitiesInCountry] = await Promise.all([
  getTopPropertiesByCityId(actualPropertyTypeObj?.key, eventObj?.location?.city?.id)
    .then((res) => res || [])
    .catch(() => []),
  getEventsByCityNameAndCountryName(
    eventObj?.location?.city?.urlFriendlyName,
    eventObj?.location?.country?.urlFriendlyName,
    EVENTSLIMIT,
  )
    .then((res) => res || [])
    .catch(() => []),
  getCountryTopCities(eventObj?.location?.country?.id)
    .then((res) => res || [])
    .catch(() => []),
]);

const eventNameSlug = useFormatter.normalizeUrl(eventObj.name);
const eventYear = eventObj?.startDate?.slice(0, 4) || new Date().getFullYear();

// ### COMPUTED
const eventDetailsPageUrl = computed(() => getEventPageUrlFromRawEvent(eventObj, t('t_OGEVENTSSLUG')));

const eventDetailsHrefLang = useUrl.generateHrefLangForEventsEventPage({
  eventId: eventObj.eventId,
  eventNameSlug,
});

const eventDetails = computed(() => buildEventDetailsModel(eventObj, eventDetailsPageUrl.value));

const eventDetailsShareMessage = computed(() => {
  return t('t_OGEVENTSHAREMESSAGE', {
    EVENTNAME: eventObj.name,
    CITYNAME: eventObj?.location?.city?.name,
    COUNTRYNAME: eventObj?.location?.country?.name,
  });
});

const eventDetailsSharePopupHeading = computed(() => {
  return t('t_OGEVENTSHAREPOPUPHEADING');
});

const getCrumbsShownByViewport = computed(() => {
  return toValue(isExtraSmallScreen) ? 2 : null;
});

const moreEventsInEventCity = computed(() => {
  const events = topEventsInLocation?.events || [];
  const rootSlug = t('t_OGEVENTSSLUG');

  const locationData = {
    continent: eventObj?.location?.continent,
    country: eventObj?.location?.country,
    city: eventObj?.location?.city,
  };

  return events
    .filter((event) => event?.eventId !== eventObj?.eventId)
    .map((event) => {
      event = {
        ...event,
        location: { ...locationData },
      };
      const pageUrl = getEventPageUrlFromRawEvent(event, rootSlug);
      return buildEventDetailsModel(event, pageUrl);
    });
});

const hasFaq = computed(() => {
  return eventDetails?.value?.faqs?.length > 0 || false;
});

const eventFaqs = computed(() => {
  if (!hasFaq.value) {
    return [];
  }

  const transcodeInterpolate = new TranscodeInterpolate();

  const faqPlaceholders = {
    EVENTNAME: eventObj?.name,
    YEAR: eventYear,
    LOCATIONNAME: eventObj?.location?.city?.name,
    PROPCOUNT: 112, // TODO: Get properties count from API later on. For now it's fine to hardcode it.
  };

  return eventDetails?.value?.faqs?.map((faq) => {
    return {
      header: transcodeInterpolate.interpolate(faq.question, faqPlaceholders),
      content: transcodeInterpolate.interpolate(faq.answer, faqPlaceholders),
    };
  });
});

function onFaqExpand(faqIndex) {
  $tracking?.onTrackEvent({
    action: 'FAQ Click',
    page_type: segmentTracking.TYPE,
    name: segmentTracking.NAME,
    occasion_genius_faq: faqIndex ?? '',
    occasion_genius_event_id: eventObj?.eventId,
    occasion_genius_event_city: eventObj?.location?.city?.name,
    occasion_genius_event_country: eventObj?.location?.country?.name,
    occasion_genius_event_start_date: eventObj?.startDate,
    occasion_genius_event_end_date: eventObj?.endDate,
  });
}

const eventTopCitiesInCountry = computed(() => {
  if (!topCitiesInCountry?.length) {
    return [];
  }

  const seenCitySlugs = new Set();
  const otherCities = [];

  for (const topCity of topCitiesInCountry) {
    if (
      topCity?.id === eventObj?.location?.cityId
      || topCity?.urlFriendlyName === eventObj?.location?.city?.urlFriendlyName
    ) {
      continue;
    }
    const urlFriendlyCitySlug = topCity?.urlFriendlyName;
    if (urlFriendlyCitySlug) {
      if (seenCitySlugs.has(urlFriendlyCitySlug)) {
        continue;
      }
      seenCitySlugs.add(urlFriendlyCitySlug);
    }

    const locationData = {
      propertyTypeSlug: t(actualPropertyTypeObj.slug),
      urlFriendlyContinent: topCity.urlFriendlyContinent,
      urlFriendlyCountry: topCity.urlFriendlyCountry,
      urlFriendlyCity: topCity.urlFriendlyName,
    };
    otherCities.push({
      id: topCity.id,
      name: topCity.name,
      label: topCity.name,
      title: topCity.name,
      url: useUrl.getCityPageUrl(locationData),
    });
  }

  return otherCities;
});

// ### BREADCRUMBS
const crumbs = [
  {
    label: t('t_HOME'),
    title: t('t_HOME'),
    link: useUrl.getStaticBaseURL(),
  },
  {
    label: t('t_A11YOGEVENTS'),
    title: t('t_OGEVENTS'),
    link: useUrl.getEventsHomeUrl({ rootSlug: t('t_OGEVENTSSLUG') }),
  },
  ...(isEventsContinentPageEnabled
    ? [{
      label: t('t_A11YOGEVENTSLISTLOCATION', {
        LOCATIONNAME: eventObj?.location?.continent?.name,
        PROPERTYTYPE: t('t_OGEVENTS'),
      }),
      title: t('t_A11YBCTEXTLOCATION', {
        LOCATIONNAME: eventObj?.location?.continent?.name,
        PROPERTYTYPE: t('t_OGEVENTS'),
      }),
      link: useUrl.getEventsContinentPageUrl({
        rootSlug: t('t_OGEVENTSSLUG'),
        urlFriendlyContinent: eventObj?.location?.continent?.urlFriendlyName,
      }),
    }]
    : []),
  ...(isEventsCountryPageEnabled
    ? [{
      label: t('t_A11YOGEVENTSLISTLOCATION', {
        LOCATIONNAME: eventObj?.location?.country?.name,
        PROPERTYTYPE: t('t_OGEVENTS'),
      }),
      title: t('t_A11YBCTEXTLOCATION', {
        LOCATIONNAME: eventObj?.location?.country?.name,
        PROPERTYTYPE: t('t_OGEVENTS'),
      }),
      link: useUrl.getEventsCountryPageUrl({
        rootSlug: t('t_OGEVENTSSLUG'),
        urlFriendlyContinent: eventObj?.location?.continent?.urlFriendlyName,
        urlFriendlyCountry: eventObj?.location?.country?.urlFriendlyName,
      }),
    }]
    : []),
  {
    label: t('t_A11YOGEVENTSLISTLOCATION', {
      LOCATIONNAME: eventObj?.location?.city?.name,
      PROPERTYTYPE: t('t_OGEVENTS'),
    }),
    title: t('t_A11YBCTEXTLOCATION', {
      LOCATIONNAME: eventObj?.location?.city?.name,
      PROPERTYTYPE: t('t_OGEVENTS'),
    }),
    link: useUrl.getEventsCityPageUrl({
      rootSlug: t('t_OGEVENTSSLUG'),
      urlFriendlyContinent: eventObj?.location?.continent?.urlFriendlyName,
      urlFriendlyCountry: eventObj?.location?.country?.urlFriendlyName,
      urlFriendlyCity: eventObj?.location?.city?.urlFriendlyName,
    }),
  },
  {
    link: '#',
    title: eventObj?.name,
  },
];

// ### SEO
// TODO: Fetch SEO data for single event from SPAPI when implemented.
const seoData = {
  title: t('t_OGEVENTSPAGETITLE', {
    EVENTNAME: eventDetails.value?.name,
    YEAR: eventYear,
    LOCATIONNAME: eventObj?.location?.city?.name,
  }),
  description: t('t_OGEVENTSPAGEDESCRIPTION', {
    EVENTNAME: eventDetails.value?.name,
    YEAR: eventYear,
    LOCATIONNAME: eventObj?.location?.city?.name,
  }),
  ogTitle: t('t_OGEVENTSPAGEOGTITLE', {
    EVENTNAME: eventDetails.value?.name,
    YEAR: eventYear,
    LOCATIONNAME: eventObj?.location?.city?.name,
  }),
  ogDescription: t('t_OGEVENTSPAGEOGDESCRIPTION', {
    EVENTNAME: eventDetails.value?.name,
    YEAR: eventYear,
    LOCATIONNAME: eventObj?.location?.city?.name,
  }),
  copy: eventDetails.value?.description,
  keywords: '',
  h1: eventDetails.value?.name,
};
const seoExtraData = {
  ogImage: eventDetails.value?.picture?.desktop,
  canonicalUrl: eventDetailsPageUrl.value,
};

const seoMetaData = buildSeo.getMetaData(seoData, seoExtraData, {}, false, locationKey);

useHead(() => ({
  htmlAttrs: { lang: locale?.value },
  link: [
    {
      rel: 'canonical',
      href: seoMetaData?.canonicalUrl,
    },
  ].concat(eventDetailsHrefLang),
}));

useSeoMeta({
  title: () => seoMetaData?.title,
  ogTitle: () => seoMetaData?.ogTitle,
  description: () => seoMetaData?.description,
  ogDescription: () => seoMetaData?.ogDescription,
  ogUrl: () => seoMetaData?.canonicalUrl,
  ogImage: () => seoMetaData?.ogImage,
  ogImageAlt: () => seoMetaData?.ogDescription,
  ogType: () => 'website',
  keywords: () => seoMetaData?.keywords,
  robots: () => 'index, follow, maxImagePreview:large',
  twitterCard: () => 'summary_large_image',
  twitterTitle: () => seoMetaData?.ogTitle,
  twitterDescription: () => seoMetaData?.ogDescription,
  twitterImage: () => seoMetaData?.ogImage,
  twitterImageAlt: () => seoMetaData?.ogDescription,
});

// ### SCHEMAS
const schemaCrumbs = crumbs?.map((item) => {
  return {
    name: item?.title,
    item: item?.link,
  };
});

useSchemaOrg([
  defineBreadcrumb({
    '@id': '#hostelworldNavigation',
    'name': 'Breadcrumb',
    'itemListElement': schemaCrumbs,
  }),
  defineEventSchema(eventDetails.value.schema),
  defineFaqSchema(hasFaq.value ? eventFaqs.value : null),
]);

// ### TRACKING
sessionStore.setEntryPoint(locationKey.toUpperCase());

const topPropertiesInLocationForTracking = topPropertiesInLocation?.map((property) => property.id) || [];
const locationEventsForTracking = moreEventsInEventCity.value?.map((event) => event.eventId ?? event.id) || [];

const commonTrackingInfo = {
  page_type: segmentTracking.TYPE,
  name: segmentTracking.NAME,
  occasion_genius_event_name: eventObj.name,
  occasion_genius_event_id: eventObj?.eventId,
  occasion_genius_event_city: eventObj?.location?.city?.name,
  occasion_genius_event_country: eventObj?.location?.country?.name,
  occasion_genius_event_start_date: eventObj?.startDate,
  occasion_genius_event_end_date: eventObj?.endDate,
  top_30_properties: topPropertiesInLocationForTracking.join('|'),
  occasion_genius_events: locationEventsForTracking.join('|'),
};

function onShareOpen() {
  $tracking?.onTrackEvent({
    event_name: segmentTracking.EVENT,
    action: 'Share Link Clicked',
    ...commonTrackingInfo,
  });
}
function onShareMethodSelect(method) {
  $tracking?.onTrackEvent({
    event_name: segmentTracking.EVENT,
    action: 'Share Link Success',
    share_og_method: method,
    ...commonTrackingInfo,
  });
}

onMounted(() => {
  const trackingInfo = {
    view: {
      event_name: segmentTracking.NAME,
      ...commonTrackingInfo,
    },
    track: {
      event_name: segmentTracking.VIEWED,
      ...commonTrackingInfo,
    },
  };

  $tracking?.triggerPageView(trackingInfo.view);
  $tracking?.triggerPageEvent(trackingInfo.track);
});
</script>

<script>
/* eslint-disable */
import { ca } from '@/lib/channelAttribution.js';
if (import.meta.client) {
  ca?.setChannelAttribution(null, 'EVENTDETAILS');
}
/* eslint-enable */
</script>

<template>
  <LazyCommonHwAppDownloadBanner v-bind="appDownloadTopBannerDetails" />

  <header class="hw-wrapper event-navbar">
    <CommonHwNavBar :show-language-pill="false" />
  </header>

  <section class="hw-wrapper event-heading-section">
    <CommonHwShare
      v-if="eventDetailsPageUrl"
      class="event-heading-share-button"
      :shareable-url="eventDetailsPageUrl"
      :share-message="eventDetailsShareMessage"
      :popup-heading="eventDetailsSharePopupHeading"
      @share-open="onShareOpen"
      @share-method-select="onShareMethodSelect"
    />
    <EventsHwEventsHeroImage
      class="event-heading-image"
      :image="eventDetails.picture"
    />
    <EventsHwEventHeading
      class="event-heading-details"
      :event-name="eventDetails.name"
      :event-location="eventDetails.locationName"
      :event-dates="eventDetails.dates"
      :event-address="eventDetails.address"
      :event-payment="eventDetails.payment"
      :event-tags="eventDetails.tags"
      :event-about="eventDetails.about"
    />

    <CommonHwGrabTheAppBanner
      class="event-heading-app-banner"
      heading-level="h2"
      :links="GRAB_THE_APP_LINKS"
    />
  </section>

  <section
    v-if="topPropertiesInLocation?.length"
    class="hw-wrapper event-nearby-accommodations"
  >
    <EventsHwEventNearbyAccommodations :properties="topPropertiesInLocation" />
  </section>

  <section
    v-if="moreEventsInEventCity?.length"
    class="hw-wrapper event-more-events-in-city"
  >
    <EventsHwMoreEventsInCity :event-list="moreEventsInEventCity" />
  </section>

  <section
    v-if="hasFaq || eventTopCitiesInCountry?.length"
    class="hw-wrapper event-faqs-and-popular-cities"
  >
    <CommonHwFaq
      v-if="hasFaq"
      class="event-faqs"
      :content="eventFaqs"
      @expand="onFaqExpand"
    />
    <CommonHwOtherLocationsInLocation
      v-if="eventTopCitiesInCountry?.length"
      class="event-popular-cities"
      :location-name="eventObj?.location?.country?.name"
      :other-locations="eventTopCitiesInCountry"
      :property-type-obj="actualPropertyTypeObj"
    />
  </section>

  <section class="hw-wrapper breadcrumbs-section">
    <WdsBreadCrumbs
      :breadcrumbs="crumbs"
      :max-crumbs-shown="getCrumbsShownByViewport"
      :reversed="false"
    />
  </section>
</template>

<style lang="scss" scoped>
.event-navbar {
  padding: 0;
}

.event-heading-section {
  display: flex;
  flex-direction: column;
  margin-bottom: $wds-spacing-xxxl;
  position: relative;

  .event-heading-share-button {
    position: absolute;
    top: wds-rem(215px);
    right: $wds-spacing-xl;
  }

  .event-heading-image {
    grid-area: hero-image;
    height: wds-rem(240px);
  }

  .event-heading-details {
    grid-area: details;
  }

  .event-heading-app-banner {
    grid-area: app-banner;
    margin-top: $wds-spacing-l;
  }

  @include tablet-large {
    .event-heading-share-button {
      top: wds-rem(535px);
      right: $wds-spacing-xl;
    }

    .event-heading-image {
      height: wds-rem(560px);
    }
  }

  @include desktop {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
      'hero-image details'
      'app-banner app-banner';
    gap: wds-rem(40px);

    .event-heading-share-button {
      top: 0;
      right: $wds-spacing-xl;
    }

    .event-heading-image {
      height: wds-rem(480px);
    }

    .event-heading-app-banner {
      margin-top: 0;
    }
  }

  @include desktop-large {
    grid-template-columns: 1fr 376px;
    grid-template-areas:
      'hero-image hero-image'
      'details app-banner';

    .event-heading-share-button {
      top: wds-rem(535px);
      right: wds-rem(56px);
    }

    .event-heading-image {
      height: wds-rem(560px);
    }
  }
}

.event-nearby-accommodations,
.event-more-events-in-city,
.event-faqs-and-popular-cities {
  margin-bottom: $wds-spacing-xxxl;
}

.event-faqs-and-popular-cities {
  .event-faqs,
  .event-popular-cities {
    :deep(h2) {
      @include title-3-bld;

      @include tablet-large {
        @include title-2-bld;
      }
    }
  }

  .event-faqs {
    margin-bottom: $wds-spacing-xxxl;
  }

  @include tablet-large {
    display: flex;
    gap: $wds-spacing-xxl;

    .event-faqs {
      flex: 1 0 50%;
      margin-bottom: 0;
    }
  }
}

.breadcrumbs-section {
  padding-left: $wds-spacing-s;

  @include tablet {
    padding-left: $wds-spacing-m;
  }

  @include desktop {
    padding-left: $wds-spacing-xl;
  }
}
</style>
